/**
 * EventLandingPage · /e/:slug
 *
 * Rediseño jul-2026 al estilo editorial claro Cines Alba/IES:
 * - Fondo cream, tipografía Archivo display uppercase + DM Sans body
 * - Rojo Cines Alba como brand default o brand del organizador
 * - Layout hero 2 columnas: poster gigante + info tabla
 * - Bloques extras (Qué incluye · Recogida · Condiciones) con divisor
 * - Checkout con card negro dramático y CTA rojo
 * - Form de compra con validación en vivo y campos nominativos condicionales
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import EntityThemeProvider from '../components/EntityThemeProvider';

interface Show {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  description?: string;
  poster_url?: string | null;
  artist_name?: string | null;
  event_datetime: string;
  doors_open_at?: string | null;
  venue_name?: string | null;
  venue_address?: string | null;
  venue_city?: string | null;
  venue_maps_url?: string | null;
  ticket_price?: number | null;
  ticket_price_min?: number | null;
  ticket_price_max?: number | null;
  capacity?: number | null;
  tickets_available?: number | null;
  status: string;
  entity_slug?: string | null;
  entity_name?: string | null;
  category?: string | null;
  event_type?: string | null;
  included?: string | null;
  terms?: string | null;
  pickup_points?: string | null;
  nominative_tickets?: boolean;
}

interface EntityBrand {
  accent_color?: string | null;
  name?: string;
}

interface ReserveResponse {
  ok: boolean;
  order_id: string;
  order_reference: string;
  total_amount: number;
  payment_url?: string | null;
  redsys_params?: {
    Ds_SignatureVersion: string;
    Ds_MerchantParameters: string;
    Ds_Signature: string;
  } | null;
}

const C = {
  bg: '#FFFFFF',
  soft: '#FAF7F4',
  cream: '#F5F0E8',
  ink: '#0A0A0A',
  muted: '#6B6660',
  faint: '#A8A29C',
  border: '#EAE6E1',
  red: '#B01820',
  fontDisplay: "'Archivo', 'Arial Narrow', sans-serif",
  fontBody: "'DM Sans', system-ui, sans-serif",
  fontMono: "'JetBrains Mono', monospace",
} as const;

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const DOW = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}
function fmtDay(iso: string): { day: string; mes: string; dow: string } {
  const d = new Date(iso);
  return {
    day: String(d.getDate()).padStart(2, '0'),
    mes: MESES[d.getMonth()],
    dow: DOW[d.getDay()],
  };
}
function unitPrice(s: Show): number | null {
  return (s.ticket_price ?? s.ticket_price_min) ?? null;
}

export default function EventLandingPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState<Show | null>(null);
  const [entityBrand, setEntityBrand] = useState<EntityBrand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    buyer_name: '', buyer_email: '', buyer_phone: '', buyer_dni: '', buyer_address: '',
    quantity: 1, price_tier: 'General',
  });

  useEffect(() => {
    if (!slug) return;
    document.title = 'Cargando… · Pagafácil';
    api.get<Show>(`/public/shows/${slug}`)
      .then((r) => {
        setShow(r.data);
        document.title = `${r.data.title} · Pagafácil`;
        // Resolver brand del organizador si existe
        if (r.data.entity_slug) {
          api.get<EntityBrand>(`/public/orgs/${r.data.entity_slug}`)
            .then((e) => setEntityBrand(e.data))
            .catch(() => {});
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Evento no encontrado'));
  }, [slug]);

  const total = useMemo(() => {
    if (!show) return 0;
    const u = unitPrice(show);
    return u ? Number(u) * form.quantity : 0;
  }, [show, form.quantity]);

  const isNominative = !!show?.nominative_tickets;
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.buyer_email);
  const canSubmit = !!show
    && !!form.buyer_name.trim()
    && validEmail
    && form.quantity >= 1
    && (!isNominative || (!!form.buyer_dni.trim() && !!form.buyer_address.trim()));

  const soldOut = show?.status === 'sold_out' || (show?.tickets_available != null && show.tickets_available <= 0);
  const cancelled = show?.status === 'cancelled';
  const draft = show?.status === 'draft';
  const brandAccent = entityBrand?.accent_color || C.red;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!show || !canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<ReserveResponse>(`/public/shows/${show.slug}/reserve`, form);
      if (r.data.redsys_params && r.data.payment_url) {
        submitRedsysForm(r.data.payment_url, r.data.redsys_params);
        return;
      }
      navigate(`/entradas/gracias?ref=${encodeURIComponent(r.data.order_reference)}&manual=1`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo iniciar el pago.');
    } finally {
      setBusy(false);
    }
  };

  if (error && !show) {
    return (
      <Shell brand={brandAccent}>
        <div style={{ padding: '120px 24px', textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
          <div style={{ fontFamily: C.fontDisplay, fontSize: 11, letterSpacing: 6, textTransform: 'uppercase', color: brandAccent, marginBottom: 16, fontWeight: 700 }}>
            404
          </div>
          <h1 style={{ fontFamily: C.fontDisplay, fontSize: 44, textTransform: 'uppercase', letterSpacing: 3, margin: '0 0 16px', fontWeight: 900 }}>
            Evento no encontrado
          </h1>
          <p style={{ color: C.muted }}>{error}</p>
          <Link to="/entradas" style={{ display: 'inline-block', marginTop: 24, padding: '12px 32px', border: `2px solid ${brandAccent}`, color: brandAccent, textDecoration: 'none', fontFamily: C.fontDisplay, fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>
            Ver cartelera
          </Link>
        </div>
      </Shell>
    );
  }

  if (!show) {
    return <Shell brand={brandAccent}><div style={{ padding: '160px 24px', textAlign: 'center', color: C.muted }}>Cargando…</div></Shell>;
  }

  const dayInfo = fmtDay(show.event_datetime);
  const unit = unitPrice(show);

  return (
    <Shell brand={brandAccent} entitySlug={show.entity_slug} entityName={entityBrand?.name || show.entity_name}>
      {/* ── Breadcrumb ── */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '24px 24px 0' }}>
        <Link
          to={show.entity_slug ? `/o/${show.entity_slug}` : '/entradas'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px',
            fontFamily: C.fontMono, fontSize: 11, letterSpacing: '0.14em',
            textTransform: 'uppercase', textDecoration: 'none',
            color: show.entity_slug ? brandAccent : C.muted,
            fontWeight: show.entity_slug ? 700 : 500,
            border: `1px solid ${show.entity_slug ? brandAccent : C.border}`,
            background: show.entity_slug ? 'rgba(255,255,255,0.6)' : 'transparent',
          }}
        >
          ← {show.entity_slug ? `Volver a ${entityBrand?.name || show.entity_name || 'cartelera'}` : 'Ver cartelera'}
        </Link>
      </div>

      {/* ── HERO 2 columnas: poster + info ── */}
      <section style={{ maxWidth: 1120, margin: '0 auto', padding: '20px 24px 40px' }}>
        {/* Chips arriba */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          <span style={{
            fontFamily: C.fontDisplay, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: '#FFF', background: brandAccent,
            padding: '5px 12px',
          }}>
            {show.category || 'Evento'}
          </span>
          {show.event_type && (
            <span style={{
              fontFamily: C.fontDisplay, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: C.muted,
              padding: '5px 12px', border: `1px solid ${C.border}`, background: C.soft,
            }}>
              {show.event_type}
            </span>
          )}
          {show.venue_city && (
            <span style={{
              fontFamily: C.fontMono, fontSize: 11, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: C.muted,
              padding: '5px 12px', border: `1px solid ${C.border}`,
            }}>
              📍 {show.venue_city}
            </span>
          )}
        </div>

        {/* Grid principal */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', border: `1px solid ${C.border}`, overflow: 'hidden' }} className="pf-hero-grid">
          {/* Poster */}
          <div style={{
            background: show.poster_url ? `url(${show.poster_url}) center/cover` : `linear-gradient(165deg,#4A0A16,#1A0508)`,
            minHeight: 440,
            position: 'relative',
          }}>
            {!show.poster_url && (
              <div style={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#FFF', fontFamily: C.fontDisplay, fontSize: 28, fontWeight: 800,
                letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center', padding: 24,
              }}>
                {show.title}
              </div>
            )}
          </div>

          {/* Info a la derecha */}
          <div style={{ padding: '40px 40px 32px', display: 'flex', flexDirection: 'column' }}>
            {show.artist_name && (
              <div style={{
                fontFamily: C.fontMono, fontSize: 11, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: brandAccent, fontWeight: 700, marginBottom: 12,
              }}>
                {show.artist_name}
              </div>
            )}
            <h1 style={{
              fontFamily: C.fontDisplay, fontSize: 'clamp(30px, 4.5vw, 52px)',
              fontWeight: 900, lineHeight: 0.98, letterSpacing: '-0.02em',
              textTransform: 'uppercase', margin: 0, color: C.ink,
            }}>
              {show.title}
            </h1>
            {show.subtitle && (
              <p style={{
                fontFamily: C.fontBody, fontSize: 16, lineHeight: 1.45,
                color: C.muted, margin: '10px 0 0',
              }}>
                {show.subtitle}
              </p>
            )}

            <div style={{ marginTop: 28, display: 'grid', gap: 14 }}>
              <InfoRow label="Fecha">{fmtDate(show.event_datetime)}</InfoRow>
              <InfoRow label="Hora">
                {fmtTime(show.event_datetime)}
                {show.doors_open_at && (
                  <span style={{ color: C.faint }}> (puertas {fmtTime(show.doors_open_at)})</span>
                )}
              </InfoRow>
              {show.venue_name && (
                <InfoRow label="Lugar">
                  <span style={{ color: C.ink }}>{show.venue_name}</span>
                  {show.venue_city && <span style={{ color: C.faint }}> · {show.venue_city}</span>}
                  {show.venue_maps_url && (
                    <a href={show.venue_maps_url} target="_blank" rel="noreferrer"
                      style={{ marginLeft: 12, color: brandAccent, fontSize: 11, fontFamily: C.fontMono, letterSpacing: 1.5, textTransform: 'uppercase', textDecoration: 'none', fontWeight: 700 }}>
                      Cómo llegar →
                    </a>
                  )}
                </InfoRow>
              )}
              {show.venue_address && (
                <InfoRow label="Dirección">
                  <span style={{ color: C.muted, fontSize: 14 }}>{show.venue_address}</span>
                </InfoRow>
              )}
            </div>

            {/* Footer del hero: día grande + precio + CTA */}
            <div style={{
              marginTop: 'auto', paddingTop: 28,
              display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap',
            }}>
              <div>
                <div style={{
                  fontFamily: C.fontDisplay, fontSize: 56, fontWeight: 900,
                  color: brandAccent, lineHeight: 0.85, fontFeatureSettings: '"tnum" 1',
                }}>
                  {dayInfo.day}
                </div>
                <div style={{
                  fontFamily: C.fontMono, fontSize: 10, color: C.faint,
                  letterSpacing: '0.14em', marginTop: 6, textTransform: 'uppercase',
                }}>
                  {dayInfo.mes} · {dayInfo.dow} · {fmtTime(show.event_datetime)}h
                </div>
              </div>
              <div style={{ flex: 1, borderLeft: `1px solid ${C.border}`, paddingLeft: 20, minWidth: 100 }}>
                <div style={{ fontFamily: C.fontMono, fontSize: 9, color: C.faint, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                  Entrada desde
                </div>
                <div style={{ fontFamily: C.fontDisplay, fontSize: 32, fontWeight: 900, color: C.ink, fontFeatureSettings: '"tnum" 1' }}>
                  {unit != null ? `${Number(unit).toFixed(2)}€` : 'A consultar'}
                </div>
              </div>
              <a href="#comprar" style={{
                padding: '14px 26px', background: brandAccent, color: '#FFF',
                textDecoration: 'none', fontFamily: C.fontDisplay, fontSize: 13,
                fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
              }}>
                Comprar →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Descripción editorial ── */}
      {show.description && (
        <section style={{ background: C.soft, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 24px' }}>
            <div style={{
              fontFamily: C.fontMono, fontSize: 10, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: brandAccent, fontWeight: 700, marginBottom: 14,
            }}>
              El evento
            </div>
            <div style={{
              fontFamily: C.fontBody, fontSize: 16, lineHeight: 1.75,
              color: C.ink, whiteSpace: 'pre-wrap',
            }}>
              {show.description}
            </div>
          </div>
        </section>
      )}

      {/* ── Bloques Extras ── */}
      {(show.included || show.pickup_points || show.terms) && (
        <section style={{ maxWidth: 1120, margin: '0 auto', padding: '56px 24px' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 40,
          }}>
            {show.included && <ExtraBlock label="Qué incluye" text={show.included} accent={brandAccent} />}
            {show.pickup_points && <ExtraBlock label="Puntos de recogida" text={show.pickup_points} accent={brandAccent} />}
            {show.terms && <ExtraBlock label="Condiciones" text={show.terms} accent={brandAccent} />}
          </div>
        </section>
      )}

      {/* ── PANEL COMPRA ── */}
      <section id="comprar" style={{ background: C.ink, borderTop: `4px solid ${brandAccent}`, padding: '56px 24px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          {cancelled ? (
            <StateBanner tone="alert" title="Evento cancelado" text="Si compraste entrada, te devolveremos el importe íntegramente." />
          ) : draft ? (
            <StateBanner tone="info" title="Aún no a la venta" text="Anunciaremos pronto cuándo abren las entradas." />
          ) : soldOut ? (
            <StateBanner tone="info" title="Entradas agotadas" text="No quedan localidades disponibles." />
          ) : (
            <form onSubmit={submit}>
              <div style={{
                fontFamily: C.fontMono, fontSize: 11, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: brandAccent, fontWeight: 700, marginBottom: 8,
              }}>
                Comprar entradas
              </div>
              <h2 style={{
                fontFamily: C.fontDisplay, fontSize: 'clamp(26px, 3.5vw, 38px)',
                fontWeight: 900, letterSpacing: '0.02em', textTransform: 'uppercase',
                margin: '0 0 24px', color: '#FFF',
              }}>
                Reserva tu entrada
              </h2>

              {isNominative && (
                <div style={{
                  marginBottom: 20, padding: '12px 16px',
                  background: 'rgba(255,255,255,0.06)', border: `1px solid ${brandAccent}`,
                  fontFamily: C.fontBody, fontSize: 13, color: '#f5f0e8',
                }}>
                  <b style={{ color: brandAccent }}>Entradas nominativas.</b> Este evento requiere DNI y dirección de cada comprador.
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="pf-form-grid">
                <Field label="Nombre y apellidos" required>
                  <input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} required style={inputStyle} />
                </Field>
                <Field label="Email" required>
                  <input type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} required style={inputStyle} />
                </Field>
                <Field label="Teléfono">
                  <input value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Cantidad">
                  <input type="number" min={1} max={20} value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: Math.max(1, Number(e.target.value) || 1) })}
                    style={inputStyle} />
                </Field>
                {isNominative && (
                  <>
                    <Field label="DNI / NIE" required>
                      <input value={form.buyer_dni}
                        onChange={(e) => setForm({ ...form, buyer_dni: e.target.value.toUpperCase() })}
                        required style={inputStyle} placeholder="12345678A" />
                    </Field>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Field label="Dirección postal" required>
                        <input value={form.buyer_address}
                          onChange={(e) => setForm({ ...form, buyer_address: e.target.value })}
                          required style={inputStyle}
                          placeholder="Calle, número, piso, CP, ciudad" />
                      </Field>
                    </div>
                  </>
                )}
              </div>

              {error && (
                <div style={{
                  marginTop: 18, padding: '12px 16px',
                  background: 'rgba(225,26,26,0.15)', border: `1px solid ${brandAccent}`,
                  color: '#FFB4B4', fontFamily: C.fontBody, fontSize: 13,
                }}>
                  {error}
                </div>
              )}

              <div style={{
                marginTop: 28, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.12)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: 20,
              }}>
                <div>
                  <div style={{ fontFamily: C.fontMono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.faint, marginBottom: 4 }}>
                    Total
                  </div>
                  <div style={{ fontFamily: C.fontDisplay, fontSize: 40, fontWeight: 900, color: '#FFF', fontFeatureSettings: '"tnum" 1' }}>
                    {total.toFixed(2)} €
                  </div>
                </div>
                <button type="submit" disabled={!canSubmit || busy} style={{
                  background: canSubmit && !busy ? brandAccent : '#333',
                  color: '#FFF', border: 'none',
                  padding: '18px 32px',
                  fontFamily: C.fontDisplay, fontSize: 13,
                  fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase',
                  cursor: canSubmit && !busy ? 'pointer' : 'not-allowed',
                }}>
                  {busy ? 'Redirigiendo…' : 'Pagar · Bizum / Tarjeta →'}
                </button>
              </div>

              <div style={{
                marginTop: 16, fontFamily: C.fontMono, fontSize: 10,
                letterSpacing: '0.1em', color: C.faint, textTransform: 'uppercase',
              }}>
                Pago procesado por Redsys · Entrada digital con QR nominativo por email
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer accent={brandAccent} entitySlug={show.entity_slug} entityName={entityBrand?.name || show.entity_name} />

      <style>{`
        @media (max-width: 780px) {
          .pf-hero-grid { grid-template-columns: 1fr !important; }
          .pf-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Shell>
  );
}

function Shell({
  children, brand, entitySlug, entityName,
}: {
  children: React.ReactNode; brand: string; entitySlug?: string | null; entityName?: string | null;
}) {
  return (
    <EntityThemeProvider accentColor={brand}>
      <div style={{ fontFamily: C.fontBody, color: C.ink, background: C.bg, minHeight: '100vh' }}>
        <Nav accent={brand} entitySlug={entitySlug} entityName={entityName} />
        {children}
      </div>
    </EntityThemeProvider>
  );
}

function Nav({
  accent, entitySlug, entityName,
}: {
  accent: string; entitySlug?: string | null; entityName?: string | null;
}) {
  // Cuando el evento pertenece a un organizador, el link derecho vuelve a
  // SU cartelera (no a la global). Fix Cuervo · jul-2026: reportado que el
  // botón "Cartelera" del nav llevaba a /entradas mezclando eventos de todas
  // las orgs; el operador quería volver a su propia landing.
  const backTo = entitySlug ? `/o/${entitySlug}` : '/entradas';
  const backLabel = entitySlug
    ? `← ${entityName || 'Volver al organizador'}`
    : 'Cartelera';
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 24px', background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(10px)', borderBottom: `1px solid ${C.border}`,
    }}>
      <Link to="/" style={{
        display: 'flex', alignItems: 'center', gap: 8, color: C.ink, textDecoration: 'none',
        fontFamily: C.fontDisplay, fontSize: 14, fontWeight: 900,
        letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, background: accent, borderRadius: '50%' }} />
        Pagafácil
      </Link>
      <Link to={backTo} style={{
        color: entitySlug ? accent : C.muted, textDecoration: 'none',
        fontFamily: C.fontMono, fontSize: 11,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        fontWeight: entitySlug ? 700 : 400,
        maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {backLabel}
      </Link>
    </nav>
  );
}

function Footer({
  accent, entitySlug, entityName,
}: {
  accent: string; entitySlug?: string | null; entityName?: string | null;
}) {
  return (
    <footer style={{
      padding: '28px 24px', borderTop: `1px solid ${C.border}`, background: C.soft,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, fontSize: 12, fontFamily: C.fontMono, letterSpacing: '0.1em', color: C.muted,
      textTransform: 'uppercase',
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 7, height: 7, background: accent, borderRadius: '50%' }} />
        {entityName ? `${entityName} · via Pagafácil` : 'Pagafácil · sin app · sin login'}
      </span>
      <a href="mailto:hola@pagafacil.eu" style={{ color: C.muted, textDecoration: 'none' }}>
        hola@pagafacil.eu
      </a>
      {entitySlug ? (
        <Link to={`/o/${entitySlug}`} style={{ color: C.muted, textDecoration: 'none' }}>
          ← {entityName || 'Volver'}
        </Link>
      ) : (
        <Link to="/entradas" style={{ color: C.muted, textDecoration: 'none' }}>Cartelera</Link>
      )}
    </footer>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 14, alignItems: 'baseline' }}>
      <div style={{
        fontFamily: C.fontMono, fontSize: 10, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: C.faint, fontWeight: 700,
      }}>
        {label}
      </div>
      <div style={{ fontFamily: C.fontBody, fontSize: 15, color: C.ink }}>
        {children}
      </div>
    </div>
  );
}

function ExtraBlock({ label, text, accent }: { label: string; text: string; accent: string }) {
  return (
    <div>
      <div style={{
        fontFamily: C.fontMono, fontSize: 10, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: accent, fontWeight: 700, marginBottom: 12,
      }}>
        · {label}
      </div>
      <div style={{
        fontFamily: C.fontBody, fontSize: 14, lineHeight: 1.65,
        color: C.ink, whiteSpace: 'pre-wrap',
      }}>
        {text}
      </div>
    </div>
  );
}

function StateBanner({ tone, title, text }: { tone: 'alert' | 'info'; title: string; text: string }) {
  const bg = tone === 'alert' ? '#c0392b' : '#1a1a1a';
  return (
    <div style={{
      background: bg, padding: '28px 32px', border: '1px solid #222',
      fontFamily: C.fontBody, fontSize: 15, textAlign: 'center', color: '#FFF',
    }}>
      <div style={{
        fontFamily: C.fontDisplay, fontSize: 22, fontWeight: 900,
        letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8,
      }}>
        {title}
      </div>
      {text}
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontFamily: C.fontMono, fontSize: 10, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: C.faint, fontWeight: 700,
      }}>
        {label}{required && <span style={{ color: '#c0392b' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#FFF',
  fontSize: 15,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  outline: 'none',
};

function submitRedsysForm(url: string, params: { Ds_SignatureVersion: string; Ds_MerchantParameters: string; Ds_Signature: string }) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.style.display = 'none';
  for (const [k, v] of Object.entries(params)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = k;
    input.value = v;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}
